import { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import axiosClient from '../api/axiosClient';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Typography from './Typography';
import { theme } from '../theme';

const LoginWithToken = () => {
  const { t } = useTranslation('auth');
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const { refreshUserData, setToken } = useAuth();
  const location = useLocation();

  useEffect(() => {
    const loginWithToken = async () => {
      try {
        const token = new URLSearchParams(location.search).get('token');
        const response = await axiosClient.get(`/api/users/login-with-token?token=${token}`);


        if (response.data.success) {
          localStorage.setItem('authToken', response.data.authToken);
          setToken(response.data.authToken);

          // Hacer la llamada directamente con el nuevo token para evitar problemas de timing
          try {
            const userResponse = await axiosClient.get('/api/users/profile', {
              headers: {
                Authorization: `Bearer ${response.data.authToken}`
              }
            });

            if (userResponse.data.success) {
              const updatedUser = userResponse.data.user;
              // Leer returnTo de la URL (viene del callback de Google/Facebook)
              const urlReturnTo = new URLSearchParams(location.search).get('returnTo');
              const destination = urlReturnTo || response.data.returnTo || '/discover';

              // Navegar según el estado del perfil
              if (!updatedUser.profileFilled) {
                navigate(
                  `/user/${updatedUser.id}/complete-profile`,
                  { state: { returnTo: destination }, replace: true }
                );
              } else {
                navigate(destination);
              }
            } else {
              console.warn('Failed to load user profile, redirecting to signin');
              navigate('/signin', { replace: true });
            }
          } catch (profileError) {
            console.error('Error loading user profile:', profileError);
            setError(t('loginWithToken.profileLoadError'));
            navigate('/signin', { replace: true });
          }
        }
      } catch (error) {
        console.error('Error in loginWithToken:', error);

        // Solo mostrar errores específicos que requieren acción del usuario
        if (error.response?.data?.error === 'TokenExpiredError') {
          setError(t('loginWithToken.tokenExpired'));
        } else if (error.response?.status === 404) {
          setError(t('loginWithToken.userNotFound'));
        } else if (error.response?.status >= 500) {
          setError(t('loginWithToken.serverError'));
        } else {
          // Para otros errores, redirigir al login sin mostrar mensaje
          console.warn('Redirecting to login due to authentication error');
          navigate('/signin', { replace: true });
        }
      }
    };

    loginWithToken();
  }, [navigate, refreshUserData, setToken, location.search]);


  if (error) return <div>{error}</div>;
  return <LoginMessage>
    <Typography $variant="body-2-medium" color={theme.colors.defaultWeak}>
      {t('loginWithToken.loading')}
    </Typography>
  </LoginMessage>;
};

export default LoginWithToken;

const LoginMessage = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  height: 100vh;
  justify-content: center;
  text-align: center;
  width: 100%;
`;